/**
 * **还款优惠登记**（批 `106-B` · `AC-B2.50 / B2.51`）
 *
 * 一个对话框承担三件事（三态由 `waiver` prop 分派）：
 * - `mode="create"`：单独记一笔优惠（账单明细页「记优惠」）；
 * - `mode="edit"`：改优惠（金额 / 类型 / 备注 —— 🔴 归属期不可改，契约层未开放）；
 * - 删优惠不进本对话框（走行内「删除」+ 确认）。
 *
 * ## 🔴 服务端原子完成（端侧不拼双腿）
 *
 * 记优惠 = 表行 + 1 腿 `in` 到卡（无分类、不计收支、`linked_waiver_id` 归因）**一个事务**。
 * 端侧若自己 `createTransaction` 再 `createWaiver`，中途失败会留下"腿在表行不在"的半截状态
 * （`AC-B2.49` / 批 104 教训）。
 *
 * ## 🔴 未出账单不能记优惠（D5）
 *
 * 入口只在**已出账单**的期次上给（账单页 current / 往期 tab）；unbilled tab 不渲染入口。
 * 服务端 `assertBilledBillDay` 也会再挡一道（端侧不做第二份账期推算 —— 铁律 24）。
 */

import { useEffect, useState, type ReactElement } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { currencySymbolOf, type CreditWaiverView } from '@jiaying/shared';
import { createCreditWaiver, deleteCreditWaiver, updateCreditWaiver } from '../api/endpoints.js';
import { ApiError } from '../api/http.js';
import { yuanTextToCentsOrNull } from '../features/txn-draft.js';

export interface CreditWaiverDialogProps {
  /** `create` = 新记；`edit` = 改（此时 `waiver` 必填） */
  mode: 'create' | 'edit';
  /** 编辑目标（`create` 时为 `null`） */
  waiver?: CreditWaiverView | null;
  accountId: string;
  currency: string;
  /** 归属期账单日（`create` 时必填 —— 由调用方给当前 tab 的账单日） */
  billDay: string;
  onClose: () => void;
  onDone: () => void;
}

/** 优惠类型的四个常用项（其余手填 —— 契约层 `kind` 不收窄取值） */
const WAIVER_KIND_OPTIONS = ['还款金', '立减', '活动减免', '其他'] as const;

/**
 * 🔴 **外壳与内容分离**（与 `LoanRepayDialog` 同一课）：`Dialog` 走 Portal，
 * 静态渲染拿不到内容 ⇒ spec 直接渲染 Body。
 */
export function CreditWaiverDialog(props: CreditWaiverDialogProps): ReactElement {
  return (
    <Dialog open onClose={props.onClose} fullWidth maxWidth="xs" data-testid="credit-waiver-dialog">
      <CreditWaiverDialogBody {...props} />
    </Dialog>
  );
}

export function CreditWaiverDialogBody({
  mode,
  waiver,
  accountId,
  currency,
  billDay,
  onClose,
  onDone,
}: CreditWaiverDialogProps): ReactElement {
  const qc = useQueryClient();
  const sym = currencySymbolOf(currency);
  const editing = mode === 'edit' && waiver !== undefined && waiver !== null;
  const [amountText, setAmountText] = useState(
    editing ? String(((waiver as CreditWaiverView).amount as number / 100).toFixed(2)) : '',
  );
  const [kind, setKind] = useState((waiver?.kind ?? '') as string);
  const [note, setNote] = useState((waiver?.note ?? '') as string);
  const [error, setError] = useState<string | null>(null);

  // 切换编辑目标时重置表单（对话框复用同一实例的场景）
  useEffect(() => {
    setAmountText(editing ? String(((waiver as CreditWaiverView).amount as number / 100).toFixed(2)) : '');
    setKind((waiver?.kind ?? '') as string);
    setNote((waiver?.note ?? '') as string);
    setError(null);
  }, [editing, waiver]);

  const cents = yuanTextToCentsOrNull(amountText);
  const amountBad = amountText.trim() === '' || cents === null || (cents as number) <= 0;

  const save = useMutation({
    mutationFn: () =>
      editing
        ? updateCreditWaiver((waiver as CreditWaiverView).id, {
            amount: cents as number,
            kind: kind === '' ? null : kind,
            note: note.trim() === '' ? null : note.trim(),
          })
        : createCreditWaiver({
            accountId,
            billDay,
            amount: cents as number,
            kind: kind === '' ? null : kind,
            note: note.trim() === '' ? null : note.trim(),
          }),
    onSuccess: () => {
      // 优惠同时改：账单（公式）/ 流水（落腿）/ 余额（卡欠款变少）/ 净资产
      void qc.invalidateQueries({ queryKey: ['creditBills'] });
      void qc.invalidateQueries({ queryKey: ['creditBillLegs'] });
      void qc.invalidateQueries({ queryKey: ['transactions'] });
      void qc.invalidateQueries({ queryKey: ['accounts'] });
      void qc.invalidateQueries({ queryKey: ['networth'] });
      onDone();
    },
    onError: (e: unknown) => {
      setError(e instanceof ApiError ? e.message : String(e));
    },
  });

  return (
    <>
      <DialogTitle>{editing ? '改优惠' : '记优惠'} · 账单日 {billDay}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            还款优惠直接抵掉该期该还的钱 —— 记一笔会同时减少这张卡的欠款（不产生收支）。
            归属期固定为账单日 {editing ? (waiver as CreditWaiverView).billDay : billDay}
            （要换归属期请删除后重记）。
          </Typography>

          <TextField
            required
            inputMode="decimal"
            label={`优惠金额（${sym}）`}
            placeholder="0.00"
            value={amountText}
            onChange={(e) => {
              setAmountText(e.target.value);
              setError(null);
            }}
            error={amountText !== '' && amountBad}
            helperText={amountText !== '' && amountBad ? '最多两位小数，且需大于 0' : '银行给了多少钱的减免就填多少'}
            slotProps={{ htmlInput: { 'data-testid': 'credit-waiver-amount' } }}
          />

          <TextField
            select
            label="优惠类型"
            value={kind}
            onChange={(e) => {
              setKind(e.target.value);
              setError(null);
            }}
            slotProps={{ htmlInput: { 'data-testid': 'credit-waiver-kind' } }}
          >
            <option value="">（不填）</option>
            {WAIVER_KIND_OPTIONS.map((k) => (
              <option key={k} value={k}>
                {k}
              </option>
            ))}
          </TextField>

          <TextField
            label="备注"
            value={note}
            onChange={(e) => {
              setNote(e.target.value);
              setError(null);
            }}
            slotProps={{ htmlInput: { 'data-testid': 'credit-waiver-note' } }}
          />

          {error !== null && (
            <Alert severity="error" data-testid="credit-waiver-error">
              {error}
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={save.isPending}>
          取消
        </Button>
        <Button
          variant="contained"
          disabled={save.isPending || amountBad}
          onClick={() => {
            setError(null);
            save.mutate();
          }}
          data-testid="credit-waiver-submit"
        >
          {save.isPending ? '保存中…' : '保存'}
        </Button>
      </DialogActions>
    </>
  );
}

/** 🆕 删优惠的确认对话框（`AC-B2.51`：同事务软删交易与表行 —— 端侧只发一个 DELETE） */
export function CreditWaiverDeleteDialog({
  waiver,
  currency,
  onClose,
  onDone,
}: {
  waiver: CreditWaiverView;
  currency: string;
  onClose: () => void;
  onDone: () => void;
}): ReactElement {
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const remove = useMutation({
    mutationFn: () => deleteCreditWaiver(waiver.id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['creditBills'] });
      void qc.invalidateQueries({ queryKey: ['creditBillLegs'] });
      void qc.invalidateQueries({ queryKey: ['transactions'] });
      void qc.invalidateQueries({ queryKey: ['accounts'] });
      void qc.invalidateQueries({ queryKey: ['networth'] });
      onDone();
    },
    onError: (e: unknown) => {
      setError(e instanceof ApiError ? e.message : String(e));
    },
  });

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth data-testid="credit-waiver-delete-dialog">
      <DialogTitle>删除这笔优惠？</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary">
          账单日 {waiver.billDay} 的优惠{' '}
          {currencySymbolOf(currency)}
          {(waiver.amount as number / 100).toFixed(2)}
          将被删除，对应的落腿流水一并撤销 —— 这期该还的钱会变多。
        </Typography>
        {error !== null && (
          <Alert severity="error" sx={{ mt: 1 }} data-testid="credit-waiver-delete-error">
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={remove.isPending}>
          取消
        </Button>
        <Button
          color="error"
          variant="contained"
          disabled={remove.isPending}
          onClick={() => {
            remove.mutate();
          }}
          data-testid="credit-waiver-delete-submit"
        >
          {remove.isPending ? '删除中…' : '删除'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
